import { ClientPostTypeEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Card, Text } from 'react-native-paper';

import { useAppState } from '@/features/authorization/provider/useAppState';
import { usePostCard } from '@/features/posts/components/feed/PostCardContext';
import { usePostCardHeaderContent } from '@/features/posts/components/feed/usePostCardHeaderContent';
import { useGetDorfHelferTypeName } from '@/features/posts/hooks/useGetDorfHelferTypeName';
import { PostFeedType } from '@/features/posts/types/postFeed';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    header: {
      minHeight: 32,
      paddingTop: 0,
      paddingHorizontal: 0,
      justifyContent: 'flex-start',
      alignItems: 'flex-end',
      zIndex: 1
    },
    headerContent: {
      maxWidth: '75%',
      backgroundColor: theme.colors.errorContainer,
      paddingHorizontal: 12,
      paddingVertical: 4,
      borderTopRightRadius: 12,
      borderBottomLeftRadius: 12
    },
    headerText: { color: theme.colors.onPrimary, textAlign: 'center' }
  });

export const PostHeaderInfo: FC = () => {
  const {
    startTime,
    endTime,
    allDayEvent,
    categories,
    katwarnEventCodeText,
    dorfHelferType,
    organizationId,
    postType
  } = usePostCard();
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const [headerHeight, setHeaderHeight] = useState<number>(32);
  const { isEmergencyOrganization } = useAppState();

  const dorfHelferTypeName = useGetDorfHelferTypeName(dorfHelferType);
  const isCivilProtection = !isEmptyOrNull(katwarnEventCodeText);
  const isEmergency = isEmergencyOrganization(organizationId);

  const localType = useMemo(() => {
    if (isEmergency) return PostFeedType.EMERGENCY;
    if (isCivilProtection) return PostFeedType.CIVIL_PROTECTION;
    return postType ?? ClientPostTypeEnum.Gossip;
  }, [isCivilProtection, isEmergency, postType]);

  const cardHeaderContent = usePostCardHeaderContent({
    postType: localType,
    startTime,
    endTime,
    allDayEvent,
    category: dorfHelferTypeName ?? categories
  });

  if (!cardHeaderContent) {
    return;
  }

  return (
    <Card.Content
      style={[styles.header, { marginBottom: -Math.max(headerHeight, 32) }]}
    >
      <View
        style={styles.headerContent}
        onLayout={(e) => setHeaderHeight(e.nativeEvent.layout.height)}
      >
        <Text
          style={styles.headerText}
          variant="labelLarge"
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          {cardHeaderContent}
        </Text>
      </View>
    </Card.Content>
  );
};
